import React, {
  forwardRef,
  MouseEventHandler,
  Ref,
  useImperativeHandle,
  useRef
} from 'react'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import {
  useRadioState,
  Radio,
  RadioGroup,
  RadioHTMLProps,
  RadioGroupProps
} from 'reakit/Radio'
import { space, SpaceProps } from 'styled-system'
import BaseTheme from '@zuto/theme'
import { radios, useDidMountEffect } from '@zuto/utilities'
import ErrorMessage from '../Form/ErrorMessage'

export type RadioButtonVariant =
  | 'standard'
  | 'standardProminentColourScheme'
  | 'small'
  | 'doubleColumn'
  | 'doubleColumnProminentColourScheme'
  | 'experimental'
  | 'experimentalProminentColourScheme'
  | 'experimentalDoubleColumn'
  | 'experimentalDoubleColumnProminentColourScheme'
  | 'experimentalDoubleColumnSmall'
  | 'experimentalDoubleColumnSmallHorizontal'
  | 'experimentalDoubleColumnSmallProminentColourScheme'
  | 'experimentalDoubleColumnSmallHorizontalProminentColourScheme'

export type RadioOption = {
  value: string | number
  label: string
}

type StyledRadioButtonProps = RadioHTMLProps & {
  $variant: RadioButtonVariant
  $alignment: 'left' | 'center'
  /**
   * TODO css gap property is not applying specifically for the extendable radio buttons for some reason.
   * Figure out why and get rid of this abomination.
   */
  prominentColourScheme?: boolean
}

export type RadioButtonProps = RadioHTMLProps & SpaceProps & {
  variant?: RadioButtonVariant
  defaultState?: number | string
  direction?: 'horizontal' | 'vertical'
  checkedVariant?: 'fill' | 'outline',
  alignment?: 'left' | 'center'
  options: RadioOption[]
  id: string
  /** A callback function that is triggered whenever the internal state is changed */
  valueChanged?: (_: string) => void
  /** This will be added to all radio options */
  onClick?: MouseEventHandler<HTMLLabelElement>
  error?: string | JSX.Element
  prominentColourScheme?: boolean
  removeContinueCta?: boolean
}

type StyledRadioGroupProps = RadioGroupProps & {
  $variant?: RadioButtonVariant
  $direction?: 'horizontal' | 'vertical'
  $checkedVariant?: 'fill' | 'outline'
  $prominentColourScheme?: boolean
}

export type RadioButtonHandle = {
  getValue: () => string | number | undefined,
  focus: () => void
}

export const RadioBox = styled.label<React.LabelHTMLAttributes<HTMLLabelElement> & StyledRadioButtonProps>`
  ${radios};
  text-align: ${(props) => props.$alignment};

  ${(props) => (
    (['experimentalDoubleColumnSmall', 'experimentalDoubleColumnSmallHorizontal', 'experimentalDoubleColumnSmallProminentColourScheme', 'experimentalDoubleColumnSmallHorizontalProminentColourScheme'].includes(props.$variant)
    )
      ? 'margin-top: 6.5px; margin-bottom: 0;'
      : ''
  )}
`

export const StyledRadio = styled(Radio)`
  width: 0;
  height: 0;
  opacity: 0;
  appearance: none;
  &:focus + ${RadioBox} {
    outline: unset;
  }
  position: absolute;
` // need position absolute to stop the 0 width input interfering with justify-content

const Wrapper = styled.div`
  ${space}
`

const StyledErrorMessage = styled(ErrorMessage) <React.HTMLAttributes<HTMLDivElement> & SpaceProps>`
  flex-basis: 100%;
`

const generateSpacing = (theme: BaseTheme, direction?: 'horizontal' | 'vertical', prominentColourScheme?: boolean): FlattenSimpleInterpolation => {
  if (direction === 'horizontal' && theme.space) {
    if (prominentColourScheme) {
      return css`
        ${RadioBox} {
          margin-left: 2.5px;
          margin-right: 2.5px;
          &:first-of-type {
            margin-left: 0;
          }
          &:last-of-type {
            margin-right: 0;
          }
        }
      `
    }

    return css`
      margin-left: -8px;
      margin-right: -8px;
      ${RadioBox} {
        margin-left: 8px;
        margin-right: 8px;
      }
    `
  }

  return css``
}

export const StyledRadioGroup = styled(RadioGroup) <StyledRadioGroupProps>`
  ${space};
  display: flex;
  flex-direction: ${(props) => (
    (props.$direction === 'horizontal' || props.$variant === 'doubleColumn' || props.$variant === 'experimentalDoubleColumn' || props.$variant === 'experimentalDoubleColumnSmall' || props.$variant === 'experimentalDoubleColumnSmallProminentColourScheme')
      ? 'row'
      : 'column'
  )};

  ${(props) => (['doubleColumn', 'experimentalDoubleColumn', 'experimentalDoubleColumnSmall', 'experimentalDoubleColumnSmallProminentColourScheme'].includes(props.$variant ?? '')
    ? 'flex-wrap: wrap; justify-content: space-between;'
    : ''
  )};

  ${(props) => generateSpacing(props.theme, props.$direction, props.$prominentColourScheme)}

  ${(props) => (
    props.$variant !== 'experimentalDoubleColumnSmallProminentColourScheme' && props.$variant !== 'experimentalDoubleColumnSmallHorizontalProminentColourScheme' && props.$variant !== 'experimentalDoubleColumnSmallHorizontal'
    && props.$variant !== 'experimentalDoubleColumnSmall'
      ? `input[type="radio"]:checked + ${RadioBox} {
        background-color: ${props.$checkedVariant === 'fill' ? '#1A56F1' : 'transparent'};
        color: ${props.$checkedVariant === 'fill' ? 'white' : 'black'};
      }` : ''
  )}

  /*
    We'll add 8px to the bottom in this case, as we're no longer sticking a margin on each button,
    and the outer wrapper that should define the margin is being set in a lot of different places.
  */
  ${(props) => ((props.$variant === 'experimentalDoubleColumnSmallProminentColourScheme' || props.$variant === 'experimentalDoubleColumnSmall') ? 'padding-bottom: 8px;' : '')}
`

const RadioButton = forwardRef(({
  variant = 'standard',
  defaultState = '',
  direction = 'horizontal',
  alignment = 'left',
  options,
  id,
  valueChanged,
  onClick,
  error,
  checkedVariant = 'fill',
  prominentColourScheme = false,
  removeContinueCta = false,
  ...props
}: RadioButtonProps, ref: Ref<RadioButtonHandle>): JSX.Element => {
  const radio = useRadioState({ state: defaultState })
  const radioRef = useRef<HTMLDivElement>(null)

  useDidMountEffect(() => {
    if (valueChanged && radio.state && !removeContinueCta) {
      const parsedValue = typeof radio.state === 'number' ? `${radio.state}` : radio.state
      valueChanged(parsedValue)
    }
  }, [radio.state])

  const handleOptionClick = (value: string | number) => {
    if (valueChanged) {
      // Added timeout delay here so that radio buttons show a quick preview of focused/checked state before moving to next question
      setTimeout(() => {
        const parsedValue = typeof value === 'number' ? `${value}` : value
        valueChanged(parsedValue)
      }, 150)
    }
  }

  useImperativeHandle(ref, () => ({
    getValue() {
      return radio.state
    },
    /** Gives focus to the first radio button */
    focus() {
      if (radioRef.current && radioRef.current.firstChild) {
        (radioRef.current.firstChild as HTMLElement)?.focus()
      }
    }
  }))

  // TODO Fix this, this should be split out into a separate component, so react knows
  // to treat it as a component. Otherwise, this can cause strange behaviour.
  const generateOptions = ({ value, label }: RadioOption) => {
    const optionId = `${id}+${value.toString()}`

    return (
      <React.Fragment key={value.toString()}>
        <StyledRadio
          {...radio}
          name={value.toString()}
          id={optionId}
          value={value}
          data-testid="radio-input"
        />
        <RadioBox
          className={variant}
          data-testid="radio-option"
          htmlFor={optionId}
          $variant={variant}
          $alignment={alignment}
          onClick={removeContinueCta ? () => handleOptionClick(value) : onClick}
        >
          {label}
        </RadioBox>
      </React.Fragment>
    )
  }

  const errorComponent = error && typeof error === 'string'
    ? <StyledErrorMessage>{error}</StyledErrorMessage>
    : error

  return (
    <Wrapper {...props}>
      <StyledRadioGroup
        {...radio}
        aria-label="Selection"
        $checkedVariant={checkedVariant}
        $direction={direction}
        id={id}
        ref={radioRef}
        $variant={variant}
        $prominentColourScheme={prominentColourScheme}
      >
        {options.map(generateOptions)}
      </StyledRadioGroup>
      {!!errorComponent && errorComponent}
    </Wrapper>
  )
})

export default RadioButton
